import Link from "next/link";

interface IButtonProps {
  className?: string;
  label: string | React.ReactNode;
  href?: string;
  onClick?: () => void;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  disabled?: boolean;
  _blank?: boolean;
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit" | "reset";
  variant?: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary' | 'ghost' | 'danger';
}

export const Button = ({
  className,
  label,
  onClick,
  href,
  iconLeft,
  iconRight,
  disabled = false,
  _blank = false,
  size = "md",
  type = "button",
  variant = "primary",
}: IButtonProps) => {
  const baseClasses = `flex items-center justify-center w-fit font-medium rounded-md transition-all duration-200 whitespace-nowrap gap-2`;

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm rounded-md",
    md: "px-4 py-2 text-base rounded-lg",
    lg: "px-6 py-3 text-lg rounded-lg",
  };

  const variantClasses = {
    primary: 
      "bg-primary text-white border border-transparent hover:bg-primary-dark disabled:bg-primary/50",
    secondary:
      "bg-secondary-600 text-white border border-transparent hover:bg-secondary-700 disabled:bg-secondary-600/50",
    'outline-primary': 
      "text-primary border border-primary bg-transparent hover:bg-primary hover:text-white disabled:border-primary/50 disabled:text-primary/50",
    'outline-secondary':
      "text-secondary-600 border border-secondary-600 bg-transparent hover:bg-secondary-600 hover:text-white disabled:border-secondary-600/50 disabled:text-secondary-600/50",
    ghost:
      "text-primary bg-transparent hover:bg-primary/10 disabled:text-primary/50",
    danger:
      "bg-red-600 text-white border border-transparent hover:bg-red-700 disabled:bg-red-600/50",
  };

  const disabledClasses = "opacity-75 cursor-not-allowed pointer-events-none";

  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabled ? disabledClasses : ''} ${className}`;

  if (href) {
    return (
      <Link href={href} className={buttonClasses} target={_blank ? "_blank" : undefined}>
        {iconLeft && <span>{iconLeft}</span>}
        {label}
        {iconRight && <span>{iconRight}</span>}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={buttonClasses}
      type={type}
    >
      {iconLeft && <span>{iconLeft}</span>}
      {label}
      {iconRight && <span>{iconRight}</span>}
    </button>
  );
};
